import { Box, CircularProgress, styled } from "@mui/material";
import {
  Form,
  FormState,
  Quote,
  SetFormState,
} from "@platform/carrier-tracker-components";
import { Proposal as AquafinaProposal } from "@platform/view-policy-components";
import { useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import { useApi } from "@platform/api";
import { extractErrorMessage } from "@platform/api";

import envVariables from "../../../../projectEnvVariables";

import ErrorMessage from "./error-message";

type Props = {
  rfpId: string;
  clientName: string;
  quote: Quote;
  formState: FormState;
  setFormState: SetFormState;
};

function QuoteDetails({
  quote,
  rfpId,
  formState,
  setFormState,
  clientName,
}: Props): React.JSX.Element {
  const axiosInstance = useApi();
  const refetchRef = useRef(false);

  const { data, isLoading, error, refetch, isRefetching } =
    useQuery<AquafinaProposal>({
      queryKey: ["aquafina", "rfps", "proposals", quote.ecoProposalId],
      queryFn: () => {
        const url = `${envVariables.VITE_REACT_APP_PORTAL_API_URL}/aquafina/v1/rfps/${rfpId}/proposals/${
          quote.ecoProposalId
        }${refetchRef.current ? "?forceRemapping=true" : ""}`;
        return axiosInstance.get<AquafinaProposal>(url).then((res) => res.data);
      },
      refetchOnWindowFocus: false,
    });

  if (isLoading) {
    return (
      <WrapperContent>
        <CircularProgress color="inherit" />
      </WrapperContent>
    );
  }

  if (error) {
    return (
      <WrapperContent>
        <ErrorMessage>{extractErrorMessage(error)}</ErrorMessage>
      </WrapperContent>
    );
  }

  if (!data) {
    return null;
  }

  return (
    <Form
      aquafinaProposal={data}
      quoteProposal={quote.proposal}
      clientName={clientName}
      productType={quote.productType}
      formState={formState}
      productPlanType={quote.productPlanType}
      setFormState={setFormState}
      loadingRefetch={isRefetching}
      displayV4Fields={true}
      onRefetch={() => {
        refetchRef.current = true;
        refetch();
      }}
    />
  );
}

const WrapperContent = styled(Box)(() => ({
  minHeight: 200,
  minWidth: 350,
  display: "flex",
  justifyContent: "center",
  alignItems: "center",
}));

export default QuoteDetails;
